import moment from 'moment-timezone'
import {StackedBarSeries, CardMetricSeries, ChartColors, MetricsByCard} from './types'

/**
 * Hash string to generate consistent colors
 */
export const hashString = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0 // Convert to 32bit integer
  }

  // Generate HSL color with fixed saturation and lightness for consistency
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 65%, 55%)`
}

/**
 * Get predefined or generated color for card
 */
export const getCardColor = (cardName: string): string => {
  return hashString(cardName)
}

/**
 * Generate date range for charts
 */
export const generateDates = (): string[] => {
  const startDate = moment().subtract(7, 'days')
  const dates: string[] = []

  for (let i = 0; i < 8; i++) {
    dates.push(moment(startDate).add(i, 'days').format('YYYY-MM-DD'))
  }

  return dates
}

/**
 * Clean and validate series data from API
 */
export const cleanSeriesData = (rawData: any[]): StackedBarSeries[] => {
  if (!Array.isArray(rawData)) return []

  return rawData.map((series: any) => ({
    name: String(series?.name || ''),
    data: Array.isArray(series?.data) ? series.data.map((d: any) => Number(d) || 0) : [],
  }))
}

/**
 * Clean and validate card metric series data
 */
export const cleanCardMetricSeries = (rawData: any[]): CardMetricSeries[] => {
  if (!Array.isArray(rawData)) return []

  return rawData.map((series: any) => ({
    name: String(series?.name || ''),
    data: Array.isArray(series?.data) ? series.data.map((d: any) => Number(d) || 0) : [],
  }))
}

/**
 * Format categories for charts
 */
export const formatCategories = (dates: string[]): string[] => {
  if (!Array.isArray(dates)) return []

  return dates.map((d: string) => moment(d).format('MMM DD'))
}

/**
 * Validate chart data before rendering
 */
export const isValidChartData = (data: StackedBarSeries[] | CardMetricSeries[]): boolean => {
  return data.length > 0 && data.every((s) => s.name && Array.isArray(s.data))
}

/**
 * Generate comparison data for selected card (fallback/mock data)
 */
export const generateCardComparisonData = (selectedCard: string): CardMetricSeries[] => {
  const dates = generateDates()

  // Use selected card to generate data
  const seed = selectedCard
  let hashBase = 0
  for (let i = 0; i < seed.length; i++) {
    hashBase = (hashBase << 5) - hashBase + seed.charCodeAt(i)
    hashBase |= 0
  }

  // Generate metric data based on the selected card
  return [
    {
      name: selectedCard,
      data: dates.map((date) => {
        const seedVal = seed + date
        let hash = hashBase
        for (let i = 0; i < seedVal.length; i++) {
          hash = (hash << 5) - hash + seedVal.charCodeAt(i)
          hash |= 0
        }
        // Use a different range for each card
        const baseValue = (Math.abs(hash) % 100) + 50
        // Adjust value based on card and metric
        return Math.round(baseValue)
      }),
    },
  ]
}
